'use client'

import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
type _Day = typeof DAYS[number]

const DAY_LABELS: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
}

interface DayHours {
  day: string
  open?: string
  close?: string
  closed?: boolean
}

interface HoursBlockContent {
  hours: DayHours[]
  notes?: string
}

interface HoursBlockProps {
  content: HoursBlockContent
  className?: string
}

function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`
}

function getTodayDay(): string {
  const jsDay = new Date().getDay() // 0=Sun, 1=Mon...
  return DAYS[jsDay === 0 ? 6 : jsDay - 1]
}

export function HoursBlock({ content, className }: HoursBlockProps) {
  const t = useTranslations('venue')
  if (!content.hours || content.hours.length === 0) return null

  const today = getTodayDay()

  // Ensure all days are represented, in order
  const orderedHours = DAYS.map(day =>
    content.hours.find(h => h.day === day) || { day, closed: true }
  )

  return (
    <div className={cn('space-y-3', className)}>
      <h3 className="text-lg font-semibold text-white">{t('hours')}</h3>
      <div className="space-y-1">
        {orderedHours.map((h) => {
          const isToday = h.day === today
          return (
            <div
              key={h.day}
              className={cn(
                'flex items-center justify-between py-2 px-3 rounded-lg text-sm',
                isToday && 'bg-violet-500/10 border border-violet-500/20'
              )}
            >
              <span className={cn(
                'font-medium',
                isToday ? 'text-violet-300' : 'text-zinc-300'
              )}>
                {DAY_LABELS[h.day]}
                {isToday && <span className="text-xs ml-1.5 text-violet-400">{t('today')}</span>}
              </span>
              <span className={cn(
                isToday ? 'text-violet-300' : 'text-zinc-400'
              )}>
                {h.closed
                  ? t('closed')
                  : h.open && h.close
                    ? `${formatTime(h.open)} – ${formatTime(h.close)}`
                    : '—'}
              </span>
            </div>
          )
        })}
      </div>
      {content.notes && (
        <p className="text-xs text-zinc-500 italic">{content.notes}</p>
      )}
    </div>
  )
}
